import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import ResourceSignupForm from "@/components/ResourceSignupForm";
import CompanionPanel from "@/components/CompanionPanel";
import { RESOURCES, SEO as SEO_CONTENT } from "@/content";

const ResourceCard = ({ resource, index, defaultOpen, subscriber, onSubscriber }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Reveal delay={0.05 * index} y={20}>
      <article
        id={resource.slug}
        data-testid={`resource-card-${resource.slug}`}
        className="scroll-mt-28 border border-stone bg-white/60 p-8 md:p-12"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-teal mb-4">
              {resource.type}
            </p>
            <h2 className="font-serif text-2xl md:text-4xl text-charcoal leading-snug">
              {resource.need}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-taupe max-w-xl">
              {resource.description}
            </p>
          </div>
          <span className="hidden md:block font-serif italic text-3xl text-stone shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>

        <div className="mt-8">
          <AnimatePresence mode="wait">
            {open ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
              >
                <ResourceSignupForm
                  resource={resource}
                  subscriber={subscriber}
                  onSubscriber={onSubscriber}
                />
              </motion.div>
            ) : (
              <motion.button
                key="button"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                onClick={() => setOpen(true)}
                data-testid={`resource-receive-button-${resource.slug}`}
                className="inline-flex items-center gap-2 border border-stone text-charcoal text-xs uppercase tracking-[0.25em] px-8 py-3.5 hover:border-teal hover:text-deepteal transition-colors duration-500"
              >
                <Plus size={14} />
                Receive this free resource
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </article>
    </Reveal>
  );
};

const BeginHerePage = () => {
  const { hash } = useLocation();
  const openSlug = hash ? hash.slice(1) : null;
  const [subscriber, setSubscriber] = useState(null);

  useEffect(() => {
    if (openSlug) {
      const t = setTimeout(() => {
        document.getElementById(openSlug)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [openSlug]);

  return (
    <>
      <SEO title={SEO_CONTENT.beginHere.title} description={SEO_CONTENT.beginHere.description} />

      <section data-testid="begin-here-hero" className="border-b border-stone/60 relative overflow-hidden grain">
        <div
          aria-hidden="true"
          className="absolute -top-24 left-1/3 w-[36vw] max-w-[440px] aspect-square rounded-full bg-[radial-gradient(circle,rgba(76,144,128,0.11),transparent_65%)] blur-3xl anim-orb"
        />
        <div className="relative mx-auto max-w-3xl px-6 md:px-10 pt-40 md:pt-52 pb-20 md:pb-28 text-center">
          <Reveal>
            <p className="eyebrow mb-6">Free Resources</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.08]" data-testid="begin-here-title">
              A quiet place to begin.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-taupe max-w-xl mx-auto">
              You don’t have to change everything. Start with what you need
              today. Each practice and guide is free — leave your email and it
              will come to you gently.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 md:px-10 pt-16 md:pt-20" data-testid="companion-section">
        <Reveal y={20}>
          <CompanionPanel />
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl px-6 md:px-10 py-20 md:py-28" data-testid="begin-here-resources">
        <div className="space-y-8 md:space-y-10">
          {RESOURCES.filter((r) => r.published !== false).map((r, i) => (
            <ResourceCard key={r.slug} resource={r} index={i} defaultOpen={openSlug === r.slug} subscriber={subscriber} onSubscriber={setSubscriber} />
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-16 text-center text-sm text-taupe/80 tracking-wide">
            More practices, guides and excerpts are on their way.
          </p>
        </Reveal>
      </section>
    </>
  );
};

export default BeginHerePage;
